// The ecological layer's frontend contract, its config mirror and its patient-facing copy.
// `ecological_profile` rides inside the ancestry payload but is absent from AncestryTypes.ts,
// so the shape is declared here rather than widening a file another change owns.
// Producer: labiq EcologicalProfileService + config/ancestry_ecology.php.
// Evidence classification for every value below: https://wiki.mygene.com.au/Information-Technology/Xoe/services/labiq/ancestry/ecological-evidence-inventory.

import type { AncestryResult } from "../../../../types/geneCodes/AncestryTypes";

export interface EcologicalGeneNarrative {
	gene: string;
	gene_function?: string | null;
	ancestral_role?: string | null;
	mismatch_note?: string | null;
}

/** Narrative fields arrive only when at least one of the pressure's genes is signed off. */
export interface EcologicalPressureEntry {
	pressure_id: string;
	label?: string | null;
	icon?: string | null;
	genes?: EcologicalGeneNarrative[];
	description?: string | null;
	ancestral_context?: string | null;
	modern_mismatch?: string | null;
	relevance?: string | null;
}

export interface EcologicalStapleProfile {
	staple_class?: string | null;
	dairy_tradition?: string | null;
	marine_protein?: string | null;
}

export interface EcologicalContributor {
	code: string;
	probability: number;
}

/** Group → field → value. The exposure groups are absent unless the region is signed off. */
export type EcologicalAxes = Record<string, Record<string, unknown> | undefined>;

export interface EcologicalRegionProfile {
	region_id: string;
	region_name?: string | null;
	continent?: string | null;
	weight: number;
	coverage_hint?: string | null;
	populations?: EcologicalContributor[];
	exposure_levels_signed_off?: boolean;
	axes?: EcologicalAxes;
	staple_profile?: EcologicalStapleProfile | null;
	pressures?: EcologicalPressureEntry[];
}

export type EcologicalProfile = EcologicalRegionProfile[];

export type AncestryResultWithEcology = AncestryResult & {
	ecological_profile?: EcologicalProfile | null;
};

export const ecologicalProfileOf = (result?: AncestryResultWithEcology | null): EcologicalProfile =>
	Array.isArray(result?.ecological_profile) ? (result?.ecological_profile as EcologicalProfile) : [];

// ── config('ancestry_ecology') mirror ───────────────────────────────────────────

/** Mirrors config('ancestry_ecology.level_scale'). Editorial, and the only band→number map. */
export const ECOLOGICAL_LEVEL_SCALE: Record<string, number> = {
	NONE: 0,
	MINIMAL: 1,
	LOW: 1,
	VARIABLE: 2,
	MODERATE: 2,
	HIGH: 3,
	VERY_HIGH: 4,
	EXTREME: 5,
	HEAT: 3,
	COLD: 3,
	EXTREME_HEAT: 5,
	EXTREME_COLD: 5,
	HIGHLAND: 3,
	MIXED: 2,
	LOWLAND: 1,
	COASTAL: 1,
	INLAND: 0,
};

/** The 14 rows of ecological_pressures. An axis naming anything else is a defect, not a render. */
export const ECOLOGICAL_PRESSURE_IDS: readonly string[] = [
	"alcohol_fermentation",
	"coastal_mixture",
	"cold_stress",
	"extreme_photoperiod",
	"high_altitude",
	"high_pathogen",
	"high_uv",
	"lactose_pastoral",
	"low_uv",
	"novel_exposure",
	"seasonal_variability",
	"starch_agriculture",
	"summer_drought",
	"water_stress",
];

/**
 * ⚠️ The legacy defaulted an unreadable band to 1 (LOW). That fabricates a value for a field
 * that is absent or withheld, so this returns null and the axis stays unscoreable instead.
 */
export const parseLevel = (value: unknown): number | null => {
	if (value === null || value === undefined) return null;
	if (typeof value === "number") return Number.isFinite(value) ? value : null;
	if (typeof value !== "string") return null;

	const parts = value
		.toUpperCase()
		.split("|")
		.map((part) => part.trim())
		.filter(Boolean);

	const ranks = parts.map((part) => ECOLOGICAL_LEVEL_SCALE[part]).filter((rank): rank is number => typeof rank === "number");

	if (ranks.length === 0) return null;

	return ranks.reduce((sum, rank) => sum + rank, 0) / ranks.length;
};

export const readAxis = (axes: EcologicalAxes | undefined, path: string): unknown => {
	const [group, field] = path.split(".");
	if (!group || !field) return undefined;
	return axes?.[group]?.[field];
};

export interface ExplorerAxisDefinition {
	id: string;
	label: string;
	/** What the axis measures — required so an unlabelled percentage can never stand alone. */
	measures: string;
	/** What it is measured against. */
	baseline: string;
	/** One or two words. The radar has no room for the full label without it wrapping over itself. */
	short: string;
	fieldKeys: string[];
	pressures: string[];
	maxDiff: number;
	direction?: "increase_only";
}

/**
 * Mirrors config('ancestry_ecology.explorer_axes'), with one deviation recorded here:
 * uv's config max_diff of 15 belongs to the numeric uv_index_peak path, and the payload
 * carries only the ordinal solar_uv band, so this scores it on the 0–5 ordinal span.
 */
export const EXPLORER_AXES: readonly ExplorerAxisDefinition[] = [
	{
		id: "uv",
		label: "UV & pigmentation",
		measures: "Peak ultraviolet exposure of the environment, as a five-level band",
		baseline: "The ancestry-weighted ancestral environment",
		short: "UV",
		fieldKeys: ["environment.solar_uv"],
		pressures: ["high_uv", "low_uv"],
		maxDiff: 5,
	},
	{
		id: "thermal",
		label: "Thermal adaptation",
		measures: "Heat and cold load, and humidity, of the environment",
		baseline: "The ancestry-weighted ancestral environment",
		short: "Thermal",
		fieldKeys: ["environment.temperature_stress", "environment.humidity"],
		pressures: ["cold_stress"],
		maxDiff: 5,
	},
	{
		id: "altitude",
		label: "Altitude & oxygen",
		measures: "Typical elevation and how coastal the environment is",
		baseline: "The ancestry-weighted ancestral environment",
		short: "Altitude",
		fieldKeys: ["geography.elevation_profile", "geography.coastal_influence"],
		pressures: ["high_altitude"],
		maxDiff: 5,
	},
	{
		id: "diet",
		label: "Diet & metabolism",
		measures: "How compressed the growing season is and how much food storage or trade it demanded",
		baseline: "The ancestry-weighted ancestral environment",
		short: "Diet",
		fieldKeys: ["resource_dynamics.seasonal_compression", "resource_dynamics.storage_trade_relevance"],
		pressures: ["starch_agriculture", "lactose_pastoral", "coastal_mixture", "summer_drought", "seasonal_variability", "alcohol_fermentation"],
		maxDiff: 5,
	},
	{
		id: "pathogen",
		label: "Pathogen & immune",
		measures: "Historical infectious-disease burden and contact with animals",
		baseline: "The ancestry-weighted ancestral environment",
		short: "Pathogen",
		fieldKeys: ["historical_pressures.pathogen_ecology", "historical_pressures.zoonotic_contact"],
		pressures: ["high_pathogen"],
		maxDiff: 5,
	},
	{
		id: "inflammatory",
		label: "Water & inflammatory",
		measures: "Water availability and how predictable the food supply was",
		baseline: "The ancestry-weighted ancestral environment",
		short: "Water",
		// water_scarcity never existed as a pressure id; this axis had always failed on it.
		fieldKeys: ["historical_pressures.water_stress", "resource_dynamics.predictability"],
		pressures: ["water_stress"],
		maxDiff: 5,
	},
	{
		id: "novelty",
		label: "Biological novelty",
		measures: "Pollen, venomous fauna and pathogens the environment adds that the ancestral one did not have",
		baseline: "The ancestry-weighted ancestral environment — a reduction in burden is not novelty",
		short: "Novelty",
		fieldKeys: ["novel_biological_exposures.endemic_pollen", "novel_biological_exposures.venomous_fauna", "novel_biological_exposures.novel_pathogens"],
		pressures: ["novel_exposure"],
		maxDiff: 3,
		direction: "increase_only",
	},
];

export interface AxisFieldDefinition {
	path: string;
	label: string;
}

/** Rendered for every region: the plain description of a place, no sign-off required. */
// Flora and fauna were withheld here while the evidence inventory classed them unsupported;
// surfaced on Alexander's instruction (2026-08-24) along with the exposure levels.
export const DESCRIPTIVE_AXIS_FIELDS: readonly AxisFieldDefinition[] = [
	{ path: "environment.climate", label: "Climate" },
	{ path: "environment.precipitation_pattern", label: "Rainfall pattern" },
	{ path: "geography.dominant_terrain", label: "Terrain" },
	{ path: "ecology.dominant_biomes", label: "Dominant biomes" },
	{ path: "ecology.flora_profile", label: "Flora" },
	{ path: "ecology.fauna_profile", label: "Fauna" },
];

/**
 * Group order and headings for the country panel, mirroring the legacy's ENVIRONMENT / GEOGRAPHY /
 * ECOLOGY run. The key is the first segment of a field path, so a field lands in its group without
 * being listed twice.
 */
export const ECO_FIELD_GROUPS: readonly { key: string; label: string }[] = [
	{ key: "environment", label: "Environment" },
	{ key: "geography", label: "Geography" },
	{ key: "ecology", label: "Ecology" },
	{ key: "resource_dynamics", label: "Resources" },
	{ key: "historical_pressures", label: "Historical pressures" },
	{ key: "novel_biological_exposures", label: "Novel exposures" },
];

export const groupOfField = (path: string): string => path.split(".")[0] ?? "";

/** The 16 ordinal exposure levels. Withheld unless the region's sign-off key is present. */
export const EXPOSURE_AXIS_FIELDS: readonly AxisFieldDefinition[] = [
	{ path: "environment.seasonality", label: "Seasonality" },
	{ path: "environment.solar_uv", label: "Solar UV" },
	{ path: "environment.photoperiod_variance", label: "Day-length variation" },
	{ path: "environment.humidity", label: "Humidity" },
	{ path: "environment.temperature_stress", label: "Temperature stress" },
	{ path: "geography.elevation_profile", label: "Elevation" },
	{ path: "geography.coastal_influence", label: "Coastal influence" },
	{ path: "resource_dynamics.predictability", label: "Food-supply predictability" },
	{ path: "resource_dynamics.seasonal_compression", label: "Seasonal compression" },
	{ path: "resource_dynamics.storage_trade_relevance", label: "Storage and trade" },
	{ path: "historical_pressures.pathogen_ecology", label: "Pathogen burden" },
	{ path: "historical_pressures.zoonotic_contact", label: "Animal contact" },
	{ path: "historical_pressures.water_stress", label: "Water stress" },
	{ path: "novel_biological_exposures.endemic_pollen", label: "Endemic pollen" },
	{ path: "novel_biological_exposures.venomous_fauna", label: "Venomous fauna" },
	{ path: "novel_biological_exposures.novel_pathogens", label: "Local pathogens" },
];

export const EXPOSURE_FIELD_LABELS: Readonly<Record<string, string>> = Object.fromEntries(EXPOSURE_AXIS_FIELDS.map((field) => [field.path, field.label]));

export interface EcoSummaryRowDefinition {
	path: string;
	label: string;
	/** Only an INCREASE over the patient's own environment is a mismatch. */
	directional?: boolean;
	/** The legacy hid a row rather than showing a reassuring "low". */
	minLevel?: number;
}

/** EXPOSURE_AXIS_FIELDS names the field; a summary row adds only its band rules. A second name
    here is how the Explorer and the country panel ended up calling one field two things. */
const summaryRow = (path: string, rules: Omit<EcoSummaryRowDefinition, "path" | "label"> = {}): EcoSummaryRowDefinition => ({ path, label: EXPOSURE_FIELD_LABELS[path] ?? path, ...rules });

export const ECO_SUMMARY_ROWS: readonly EcoSummaryRowDefinition[] = [
	summaryRow("environment.solar_uv", { directional: true }),
	summaryRow("environment.temperature_stress"),
	summaryRow("environment.seasonality"),
	summaryRow("environment.humidity"),
	summaryRow("historical_pressures.pathogen_ecology", { directional: true }),
	summaryRow("historical_pressures.water_stress", { directional: true, minLevel: 2 }),
	summaryRow("novel_biological_exposures.endemic_pollen", { directional: true, minLevel: 2 }),
	summaryRow("novel_biological_exposures.venomous_fauna", { directional: true, minLevel: 2 }),
	summaryRow("novel_biological_exposures.novel_pathogens", { directional: true, minLevel: 2 }),
];

// ── formatting ─────────────────────────────────────────────────────────────────

const sentenceCase = (token: string): string => {
	const words = token.replace(/_/g, " ").trim().toLowerCase();
	return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A stored token has no spaces — `COASTAL|HILLS`, `Mediterranean_woodland`. Prose is left alone. */
const TOKEN_PATTERN = /^[A-Za-z0-9]+(?:_[A-Za-z0-9]+)*(?:\|[A-Za-z0-9]+(?:_[A-Za-z0-9]+)*)*$/;

export const humaniseToken = (value: unknown): string | null => {
	if (Array.isArray(value)) {
		const parts = value.map(humaniseToken).filter((part): part is string => !!part);
		return parts.length ? parts.join(", ") : null;
	}
	if (typeof value === "number") return String(value);
	if (typeof value !== "string") return null;

	const trimmed = value.trim();
	if (!trimmed) return null;
	if (!TOKEN_PATTERN.test(trimmed)) return trimmed;

	return trimmed
		.split("|")
		.map((part) => sentenceCase(part))
		.join(" / ");
};

export const formatWeight = (weight: number): string => `${(weight * 100).toFixed(1)}%`;

// ── patient-facing copy ────────────────────────────────────────────────────────
// Drafted, not approved — the same status as ancestryCopy.ts.

export const ECOLOGY_COPY_APPROVAL_STATUS = "pending-signoff" as const;

export const ECOLOGY_PENDING_REVIEW_TITLE = "Pending clinical review";

export const ECOLOGY_PENDING_REVIEW_BODY =
	"The detailed exposure levels for this environment have not been through clinical review yet, so they are withheld. The description above is what the reference data supports today.";

export const ECOLOGY_NO_PROFILE_TITLE = "No ecological information for this sample";

export const ECOLOGY_NO_PROFILE_BODY =
	"This ancestry estimate could not be linked to any reference environment. The estimate and the map are unaffected.";

export const ECOLOGY_NO_REGION_FOR_COUNTRY_TITLE = "No ancestral environment for this country";

/** The Australian case is the common one: no reference population is Australian or Papuan. */
export const ECOLOGY_NO_REGION_FOR_COUNTRY_BODY =
	"None of the reference groups behind this country's figure is linked to an ancestral environment. Some parts of the world have no reference population at all — Australia and the Pacific among them — so no ancestral environment exists for them to map to.";

export const ECOLOGY_PRESSURES_HEADING = "Environmental pressures linked to this region";

export const ECOLOGY_PRESSURES_UNREVIEWED =
	"The reference data links these pressures to this region. What each one means for a specific gene has not been reviewed, so they are named only.";

export const ECOLOGY_CONTRIBUTORS_HEADING = "Reference populations behind this figure";

export const ECOLOGY_REGIONS_HEADING = "Ancestral environments linked to this country";

export const EXPLORER_TITLE = "Genetic explorer";

export const ANCESTRY_MODE_LABEL = "Ancestry";

export const ANCESTRY_MODE_SWITCH_LABEL = "Ancestry view mode";

export const ANCESTRY_MAP_VIEW_LABEL = "Ancestry map";

export const EXPLORER_SOURCE_PROFILE_TITLE = "Ancestral environments";

export const EXPLORER_SOURCE_PROFILE_BODY =
	"The reference environments this ancestry estimate points to, and how much each one weighs in it. These are one side of the comparison; the location selected on the map supplies the other.";

export const EXPLORER_INTRO =
	"How closely the environment of the selected location resembles the environments this ancestry estimate points to. Each axis is scored on its own, and a high score means \"similar\", never \"healthy\".";

export const EXPLORER_INDEPENDENCE_NOTE =
	"A location's environment is read from the reference data for the ecological regions its map position falls in, whether or not this sample has ancestry there. It is never derived from the results, so changing the location cannot change the ancestral profile.";

export const EXPLORER_NO_COMPARISON_TITLE = "Choose a location to compare";

export const EXPLORER_NO_COMPARISON_BODY = "Select any country on the map. The explorer compares that location's environment with the ancestral environments above, whether or not this estimate points there.";

export const EXPLORER_NO_REGION_TITLE = "No environment data for this location";

/** The Americas are the common case: no ecological reference region covers North America. */
export const EXPLORER_NO_REGION_BODY =
	"No ecological reference region covers this location, so there is nothing to compare the ancestral environments against. The nearest region held is never substituted.";

export const EXPLORER_LAYER_SWITCH_LABEL = "Comparison area";

export const EXPLORER_LAYER_COUNTRY_LABEL = "Country";

export const EXPLORER_LAYER_SUBNATIONAL_LABEL = "Sub-national";

/** Only ever shown for a country we hold more than one region for, so it never promises nothing. */
export const EXPLORER_SUBNATIONAL_PROMPT = "Compare one region of this country instead of the whole of it.";

export const ECOLOGY_SUMMARY_HEADING = "Ecological summary";

export const EXPLORER_ALIGNMENT_LABEL = "Genetic alignment";

export const EXPLORER_ALIGNMENT_CAPTION = "The mean of the scored axes below. It states how closely this location's environment resembles the ancestral ones — never how healthy it is.";

export const EXPLORER_SOURCE_HEADING = "Environment read from";

export const EXPLORER_LOADING_LABEL = "Working out this location's environment…";

export const EXPLORER_AXIS_UNSCOREABLE = "Pending clinical review — not scored";

export const EXPLORER_ENVIRONMENT_ERROR_TITLE = "Environment could not be loaded";

/** Distinct from the no-coverage copy: claiming we hold no region for a place we simply failed to fetch is a false statement. */
export const EXPLORER_ENVIRONMENT_ERROR_BODY = "The reference environment for this location could not be fetched. The ancestry estimate and the map are unaffected. Select it again.";

export const EXPLORER_ALL_UNSCOREABLE_TITLE = "No axis can be scored yet";

/**
 * Shown instead of seven identical "not scored" rows with no reason. Every axis reads one of the
 * ordinal exposure levels, and none of those has cleared review — see the evidence inventory §7.1.
 */
export const EXPLORER_ALL_UNSCOREABLE_BODY =
	"Every axis here is derived from the detailed exposure levels of an environment, and none of those has been through clinical review yet, so none is scored. What the reference data supports — the description of each location, and the environments this ancestry estimate points to — is shown above and on the map.";

export const EXPLORER_SCORE_CAPTION = "Similarity to the ancestral environments";

/**
 * The legacy's traffic-light wording (`js/ancestry-map.js:1855-1871`), which describes activity
 * and never risk. Nothing renders it yet: it needs a per-pressure genotype lookup the ancestry
 * payload does not carry, and a traffic light with no genotype behind it would be a fabrication.
 */
export type GenotypeActivity = "standard" | "altered" | "significantly_altered";

export const GENOTYPE_ACTIVITY_LABEL: Record<GenotypeActivity, string> = {
	standard: "Standard activity",
	altered: "Altered activity",
	significantly_altered: "Significantly altered",
};
